import { usePageContext } from 'application/contexts/PageContext';
import { getNews } from 'application/repositories/newsRepository';
import { UI_PreviewCardProps } from 'ui/components/2-molecules/Card/PreviewCard/PreviewCard';
import useSWR from 'swr';
import { newsPreviewMapper, newsWithTagsPreviewMapper } from 'helpers/mappers';
import { useLocaleContext } from 'application/contexts/LocaleContext';
import logger from 'helpers/logger';

const getNewsFromRepository = async (
	rootId: number,
	limit: number,
	offset: number,
	preview: boolean,
	previewData: Content.PreviewData,
	filter: string,
): Promise<(News.NewsArticle | News.NewsArticleWithTags)[]> => {
	return await getNews({ rootId, limit, offset, preview, previewData, filter });
};

interface NewsFetcherResult {
	articles?: UI_PreviewCardProps[];
	isLoading: boolean;
}

export const NewsFetcher = (
	rootId: number,
	limit: number,
	offset: number,
	filter: string,
	activeTag: Tags.Tag | null,
	allTranslation: string,
): NewsFetcherResult => {
	const context = usePageContext();
	const locale = useLocaleContext();
	const { data, error } = useSWR(
		[rootId, limit, offset, context.preview, context.previewData, filter, activeTag, allTranslation] as const,
		([rootId, limit, offset, preview, previewData, filter]) => getNewsFromRepository(rootId, limit, offset, preview, previewData, filter),
		{
			revalidateOnFocus: false,
		},
	);
	if (error) {
		logger.error(`NewsFetcher.ts failed: ${error}`, error);
		throw error;
	}
	if (!data) return { isLoading: true };

	const mappedResults = data
		.map((news) => ({ ...news, date: locale.formatStringDate(news.date) }))
		.map((news) =>
			news.documentType === 'newsPageWithTags'
				? newsWithTagsPreviewMapper(news as News.NewsArticleWithTags, activeTag, allTranslation)
				: newsPreviewMapper(news),
		);
	return { articles: mappedResults, isLoading: false };
};
